import axios from 'axios';
import _ from 'lodash';
import Bluebird from 'bluebird';
import { CancellationError } from 'bluebird/js/main/errors';
import queryString from 'query-string';

import settings from './settings';

const debug = require('debug')('pointblank:api');

export class RouteChangedError extends CancellationError {
}

/**
 * @param pathRoot Path in structure to store list at
 * @param defaultParams
 * @param limit
 * @returns {Function}
 */
function paginated(pathRoot, defaultParams = {}, limit = 5) {

  const argCnt = 3;

  return function(target, key, descriptor) {
    let fn = descriptor.value;

    delete descriptor.value;
    delete descriptor.writable;

    descriptor.get = function() {
      return async function() {

        const data = arguments[0];
        const params = arguments[1] || {};
        const query = arguments[2] || {};

        const path = params.id
          ? pathRoot.concat([parseInt(params.id, 10)])
          : pathRoot;

        if (!data.getIn(path.concat(['limit']))) {
          data.setIn(path.concat(['limit']), limit);
        }
        if (!data.getIn(path.concat(['offset']))) {
          data.setIn(path.concat(['offset']), 0);
        }
        if (!data.getIn(path.concat(['query']))) {
          data.setIn(path.concat(['query']), {});
        }

        const q = { ...defaultParams, ...query };

        if (!_.isEqual(data.getIn(path.concat(['query'])).toJS(), q)) {
          data.setIn(path.concat(['offset']), 0);
        }

        data.setIn(path.concat(['query']), q);

        q.offset = data.getIn(path.concat(['offset']));
        q.limit = data.getIn(path.concat(['limit']));

        const newArgs = Array.prototype.slice.call(arguments, 0, argCnt)
          .concat(queryString.stringify(q))
          .concat(Array.prototype.slice.call(arguments, argCnt));
        const response = await fn.apply(this, newArgs);

        data.setIn(path.concat(['noMore']), response.data.length < data.getIn(path.concat(['limit'])));

        if (data.getIn(path.concat(['offset'])) === 0) {
          data.setIn(path.concat(['list']), response.data);
        } else {
          data.setIn(path.concat(['list']), data.getIn(path.concat(['list'])).toJS().concat(response.data));
        }

        data.setIn(path.concat(['offset']), data.getIn(path.concat(['offset'])) + data.getIn(path.concat(['limit'])));

        return response;
      }
    };
  }
}

export class Api {

  constructor(cookie = null) {
    this.cookie = cookie;
  }

  navInProgress = false;

  navigatingStart(data) {
    if (this.navInProgress) {
      this._rejectAllPending();
      this.navInProgress = false;
    }
    this.navInProgress = true;
  }

  navigatingEnd(data) {
    this.navInProgress = false;
    this._cleanUp();
  }

  _rejectAllPending() {
    let promises = this.promises;
    promises = { ...promises };
    _.map(promises, (promise, requestId) => {
      promise.cancel(new RouteChangedError());
      delete this.promises[requestId];
      delete this.rejectors[requestId];
      delete this.resolvers[requestId];
    });
  }

  _cleanUp() {
    this.promises = {};
  }

  static setCookies(cookies) {
    for (let cookie of cookies) {
      document.cookie = cookie;
    }
  }

  _request(method, url, data = {}) {
    debug(`${method} ${url}`);
    const apiHost = settings.apiHost() ? settings.apiHost() : '';
    const axiosConfig = {
      url: `${apiHost}${url}`,
      method,
      data
    };
    axiosConfig.headers = {
      'content-type': 'application/json'
    };
    if (this.cookie) {
      axiosConfig.headers.cookie = this.cookie;
    }
    return Bluebird.cast(axios(axiosConfig));
  }

  post(url, data = {}) {
    return this._request('post', url, data);
  }

  get(url, data = {}) {
    return this._request('get', url, data);
  }

  put(url, data = {}) {
    return this._request('put', url, data);
  }

  delete_(url) {
    return this._request('delete', url);
  }

  newsRead(id) {
    return this.get(`/api/news/${id}/`);
  }

  signin({ nickname, password }) {
    return this.post('/api/signin/', { nickname, password })
  }

  signin_4game({ jwt_token }) {
    return this.post('/api/signin_4game/', { jwt_token });
  }

  signout() {
    return this.post('/api/signout/');
  }

  flatpage(id) {
    return this.get(`/api/flatpages/${id}/`);
  }

  updateProfile({ nickname }) {
    return this.post('/api/profile/', { nickname })
  }

  leaveTeam() {
    return this.post('/api/teams/leave/');
  }

  updateProfileImage() {

  }

  @paginated(['news'])
  news(data, params, query, q) {
    return this.get(`/api/news/?${q}`);
  }

  async faq(data) {
    let response = await this.get('/api/faq/');

    data.setIn('faq', response.data);
  }

  @paginated(['teams'], { sort: '-score', query: '' })
  teams(data, params, query, q) {
    return this.get(`/api/teams/?${q}`);
  }

  @paginated(['teamsHomeTop'], { sort: '-score' })
  teamsHomeTop(data, params, query, q) {
    return this.get(`/api/teams/?${q}`);
  }

  @paginated(['users'], { sort: '-score', query: '' })
  users(data, params, query, q) {
    return this.get(`/api/users/?${q}`);
  }

  @paginated(['usersHomeTop'], { sort: '-score' })
  usersHomeTop(data, params, query, q) {
    return this.get(`/api/users/?${q}`);
  }

  @paginated(['userTournaments'])
  userTournaments(data, params, query, q) {
    return this.get(`/api/users/${params.id}/tournaments/?${q}`);
  }

  @paginated(['teamTournaments'])
  teamTournaments(data, params, query, q) {
    return this.get(`/api/teams/${params.id}/tournaments/?${q}`);
  }

  @paginated(['userGames'])
  userGames(data, params, query, q) {
    return this.get(`/api/users/${params.id}/games/?${q}`);
  }

  @paginated(['teamGames'])
  teamGames(data, params, query, q) {
    return this.get(`/api/teams/${params.id}/games/widget/?${q}`);
  }

  userRead(id) {
    return this.get(`/api/users/${id}/`);
  }

  userUpdate({ id, ...props }) {
    return this.put(`/api/users/${id}/`, props);
  }

  userInvite({ id }) {
    return this.post(`/api/users/${id}/invite/`);
  }

  userInviteCancel({ id }) {
    return this.get(`/api/users/${id}/cancel/`);
  }

  gamesRecent() {
    return this.get(`/api/games/widget/`);
  }

  gameRead(id) {
    return this.get(`/api/games/${id}/`);
  }

  gameAddComplaint(id, text) {
    return this.post(`/api/games/${id}/complaints/add/`, { text });
  }

  gameClaimResults(id, results) {
    return this.post(`/api/games/${id}/results/claim/`, { results });
  }

  teamRead(id) {
    return this.get(`/api/teams/${id}/`);
  }

  teamUpdate({ id, ...props }) {
    return this.put(`/api/teams/${id}/`, props);
  }

  teamCreate({ title }) {
    return this.post(`/api/teams/new/`, { title });
  }

  teamApply({ teamId }) {
    return this.post(`/api/teams/${teamId}/apply/`);
  }

  teamApplyCancel({ teamId }) {
    return this.post(`/api/teams/${teamId}/cancel/`);
  }

  captainSetMemberRole({ roleId, userId }) {
    return this.post(`/api/captain/set_member_role/`, { role: roleId, user_id: userId });
  }

  captainTransferTeam({ userId }) {
    return this.post(`/api/captain/transfer/`, { user_id: userId });
  }

  captainKickPlayer({ userId }) {
    return this.post(`/api/captain/kick/`, { user_id: userId });
  }

  captainApplyTeamToTournament({ tournamentId }) {
    return this.post(`/api/tournaments/${tournamentId}/apply/`);
  }

  captainConfirmTeamToTournament({ tournamentId }) {
    return this.post(`/api/tournaments/${tournamentId}/confirm/`);
  }

  invites() {
    return this.get(`/api/invites/inbox/`);
  }

  invitesAccept({ inviteId }) {
    return this.post(`/api/invites/inbox/${inviteId}/accept/`);
  }

  invitesReject({ inviteId }) {
    return this.post(`/api/invites/inbox/${inviteId}/reject/`);
  }

  applications() {
    return this.get(`/api/applications/inbox/`);
  }

  applicationsAccept({ applicationId }) {
    return this.post(`/api/applications/inbox/${applicationId}/accept/`);
  }

  applicationsReject({ applicationId }) {
    return this.post(`/api/applications/inbox/${applicationId}/reject/`);
  }

  stagesRead(id) {
    return this.get(`/api/stages/${id}/`);
  }

  @paginated(['tournaments'], {}, 20)
  tournaments(data, params, query, q) {
    const type = (query && query.type && `${query.type}/`) || '';
    return this.get(`/api/tournaments/${type}?${q}`);
  }

  tournamentsRead(id) {
    return this.get(`/api/tournaments/${id}/`);
  }

  manifest() {
    return this.get(`/api/manifest/`);
  }

}

export default new Api();



// WEBPACK FOOTER //
// ./src/api.js